import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Bell, CheckCheck, Settings2, AlertTriangle, TrendingUp, RefreshCcw, FileText, ArrowRight, SlidersHorizontal, Circle, Users, LockKeyhole, LoaderCircle } from "lucide-react";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useLocalization } from "@/lib/localization";
import { useAuth, useWorkspace } from "@/lib/store";
import { fetchNotificationInboxDetail, fetchReportRecipients, updateNotificationPreferences, updateReportRecipient } from "@/lib/shopApi";
import { ApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { parityCopy } from "@/lib/parityLocalization";
import { formatDateTime, formatRelative } from "@/lib/formatters";

const ICON_BY_TYPE = {
  "stock-out": AlertTriangle,
  "low-stock": AlertTriangle,
  "daily-summary": FileText,
  "weekly-summary": FileText,
  "monthly-report": FileText,
  "insight": TrendingUp,
  "sync": RefreshCcw,
};

const TONE_MAP = {
  danger: "bg-destructive/[0.07] text-destructive",
  warning: "bg-warning/[0.09] text-warning",
  primary: "bg-primary/[0.07] text-primary",
  info: "bg-chart-2/[0.08] text-chart-2",
  accent: "bg-muted text-foreground/70",
};

function matchesFilter(notification, filter) {
  if (filter === "all") return true;
  if (filter === "stock") return notification.type === "low-stock" || notification.type === "stock-out";
  if (filter === "sales") return notification.type === "daily-summary" || notification.type === "monthly-report";
  if (filter === "insights") return notification.type === "insight" || notification.type === "weekly-summary";
  return notification.type === "sync";
}

function routeForNotification(notification) {
  if (notification.type === "sync") return "/settings";
  if (notification.type === "low-stock" || notification.type === "stock-out") return "/inventory";
  if (notification.type === "daily-summary" || notification.type === "monthly-report" || notification.type === "weekly-summary") return "/analytics";
  if (notification.type === "insight") return "/analytics";
  return null;
}

function notificationTimestamp(notification) {
	const candidates = [notification?.createdAtIso, notification?.createdAtISO, notification?.createdAt];
	return candidates.find((value) => value && Number.isFinite(new Date(value).getTime())) ?? null;
}

function getFilters(copy) {
  return [
    { id: "all", label: copy.filterAll },
    { id: "stock", label: copy.filterStock },
    { id: "sales", label: copy.filterSales },
    { id: "sync", label: copy.filterSync },
    { id: "insights", label: copy.filterInsights },
  ];
}

function getLocalizedPreference(preference, copy) {
  const map = {
	billing: { label: "Billing & subscription", description: "Receipts, renewals, payment and plan alerts" },
	daily_summary: { label: copy.prefDailySummary, description: copy.prefDailySummaryDesc },
	low_stock: { label: copy.prefLowStock, description: copy.prefLowStockDesc },
	weekly_insights: { label: copy.prefWeeklyInsights, description: copy.prefWeeklyInsightsDesc },
	monthly_reports: { label: copy.prefMonthlyReports, description: copy.prefMonthlyReportsDesc },
	analytics_alerts: { label: "Analytics alerts", description: "Important trends and business insights" },
  };
  return { ...preference, ...(map[preference.id] || {}) };
}

function getLocalizedNotification(notification, copy) {
  if (notification.id === "local-sync-pending" || notification.type === "sync") {
    const countMatch = String(notification.body || "").match(/(\d+)/);
    const count = Number(countMatch?.[1] || 0);
    return {
      ...notification,
      actionLabel: copy.reviewSync,
      body: copy.queuedRecords(count),
      group: notification.group === "Today" ? copy.today : notification.group,
      title: notification.tone === "danger" ? copy.syncNeedsAttention : copy.syncPending,
    };
  }

  if (notification.type === "stock-out") {
    const name = String(notification.body || "").replace(/ is out of stock\.?$/i, "");
    return {
      ...notification,
      actionLabel: copy.addStock,
      body: copy.outOfStockBody(name),
      group: notification.group === "Today" ? copy.today : notification.group,
      title: copy.stockOut,
    };
  }

  if (notification.type === "low-stock") {
    const match = String(notification.body || "").match(/^(.*) has (\d+) left\.?$/i);
    const name = match?.[1] || notification.title;
    const count = Number(match?.[2] || 0);
    return {
      ...notification,
      actionLabel: copy.addStock,
      body: copy.lowStockBody(name, count),
      group: notification.group === "Today" ? copy.today : notification.group,
      title: copy.lowStock,
    };
  }

  return {
    ...notification,
    group: notification.group === "Today" ? copy.today : notification.group === "Earlier" ? copy.earlier : notification.group,
  };
}

export default function Notifications() {
  const navigate = useNavigate();
  const { locale, messages } = useLocalization();
  const copy = messages.notificationsPage;
  const { session } = useAuth();
  const { markAllNotificationsRead, markNotificationRead, notifications, notificationPrefs, reload, unreadNotifications } = useWorkspace();
  const [prefs, setPrefs] = useState(notificationPrefs);
  const [selected, setSelected] = useState(null);
  const [activeView, setActiveView] = useState("inbox");
  const [activeFilter, setActiveFilter] = useState("all");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [reportRecipients, setReportRecipients] = useState([]);
  const [reportRecipientsState, setReportRecipientsState] = useState("idle");
  const [busyRecipientId, setBusyRecipientId] = useState("");
  const isAccountOwner = session?.role === "Owner";
  const filters = useMemo(() => getFilters(copy), [copy]);
  const localizedPrefs = useMemo(() => prefs.map((item) => getLocalizedPreference(item, copy)), [copy, prefs]);
  const localizedInbox = useMemo(() => notifications.inbox.map((item) => getLocalizedNotification(item, copy)), [copy, notifications.inbox]);
  const unreadIds = useMemo(() => new Set(unreadNotifications.map((item) => item.id)), [unreadNotifications]);

  useEffect(() => {
    setPrefs(notificationPrefs);
  }, [notificationPrefs]);

  useEffect(() => {
    if (!selected && notifications.inbox.length) {
      setSelected(localizedInbox[0]);
    }
  }, [localizedInbox, notifications.inbox.length, selected]);

  const filteredNotifications = useMemo(
    () => localizedInbox.filter((notification) => matchesFilter(notification, activeFilter)),
    [activeFilter, localizedInbox]
  );

  useEffect(() => {
    if (!filteredNotifications.length) {
      setSelected(null);
      return;
    }

    if (!selected || !filteredNotifications.some((notification) => notification.id === selected.id)) {
      setSelected(filteredNotifications[0]);
    }
  }, [filteredNotifications, selected]);

  useEffect(() => {
    if (!selected?.id || selected.id.startsWith("local-")) {
      setDetail(null);
      return;
    }
    let active = true;
    setDetailLoading(true);
    void fetchNotificationInboxDetail(session?.sessionId, selected.id)
      .then((response) => { if (active) setDetail(response?.notificationDetail ?? null); })
      .catch(() => { if (active) setDetail(null); })
      .finally(() => { if (active) setDetailLoading(false); });
    return () => { active = false; };
  }, [selected?.id, session?.sessionId]);

  const loadReportRecipients = useCallback(async () => {
    if (!session?.sessionId || !isAccountOwner) return;
    setReportRecipientsState("loading");
    try {
      const recipients = await fetchReportRecipients(session.sessionId);
      setReportRecipients(recipients);
      setReportRecipientsState("ready");
    } catch (error) {
      if (error instanceof ApiError && error.code === "TEAM_REPORTS_PLAN_REQUIRED") {
        setReportRecipients([]);
        setReportRecipientsState("locked");
        return;
      }
      setReportRecipientsState("error");
    }
  }, [isAccountOwner, session?.sessionId]);

  useEffect(() => {
    if (activeView !== "prefs" || !isAccountOwner) return;
    void loadReportRecipients();
  }, [activeView, isAccountOwner, loadReportRecipients]);

  const handleReportRecipientToggle = async (recipient, period) => {
    if (!session?.sessionId || recipient.isOwner || busyRecipientId) return;
    const updated = { ...recipient, [period]: !recipient[period] };
    setReportRecipients((current) => current.map((item) => item.userId === recipient.userId ? updated : item));
    setBusyRecipientId(recipient.userId);
    try {
      const saved = await updateReportRecipient(session.sessionId, updated);
      setReportRecipients((current) => current.map((item) => item.userId === saved.userId ? saved : item));
      toast.success(copy.teamReportsUpdated, { description: saved.name });
    } catch (error) {
      setReportRecipients((current) => current.map((item) => item.userId === recipient.userId ? recipient : item));
      if (error instanceof ApiError && error.code === "TEAM_REPORTS_PLAN_REQUIRED") {
        setReportRecipientsState("locked");
      }
      toast.error(error?.message || copy.teamReportsUpdateFailed);
    } finally {
      setBusyRecipientId("");
    }
  };

  const grouped = useMemo(() => {
    const map = {};
    filteredNotifications.forEach((n) => (map[n.group] ||= []).push(n));
    return Object.entries(map);
  }, [filteredNotifications]);

  const filterCounts = useMemo(() => (
    Object.fromEntries(filters.map((filter) => [
      filter.id,
      localizedInbox.filter((notification) => matchesFilter(notification, filter.id)).length,
    ]))
  ), [filters, localizedInbox]);

  const enabledPrefs = localizedPrefs.filter((preference) => preference.enabled).length;

  const handleNotificationAction = async (notification) => {
    markNotificationRead(notification.id);
    const route = routeForNotification(notification);
    if (notification.type === "sync") {
      await reload();
    }
    if (route) {
      navigate(route);
    }
  };

  return (
    <PageContainer className="max-w-[1360px]">
      <div>
        <header className="flex flex-col gap-4 border-b border-border/70 pb-5 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary/[0.08] text-primary ring-1 ring-primary/10">
              <Bell className="h-[18px] w-[18px]" />
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-semibold tracking-[-0.035em]">{copy.title}</h1>
                <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-[10px] font-semibold ${
                  unreadNotifications.length ? "bg-primary/[0.08] text-primary" : "bg-emerald-50 text-emerald-700"
                }`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${unreadNotifications.length ? "bg-primary" : "bg-emerald-500"}`} />
                  {unreadNotifications.length} {copy.unread.toLowerCase()}
                </span>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">{copy.subtitle}</p>
              <p className="mt-1 text-[11px] text-muted-foreground">
                {localizedInbox.length} {copy.messages.toLowerCase()} · {enabledPrefs}/{localizedPrefs.length || 0} {copy.channelsOn.toLowerCase()}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="h-10 rounded-xl px-4"
            data-testid="notifications-mark-all-read"
            onClick={markAllNotificationsRead}
            disabled={!unreadNotifications.length}
          >
            <CheckCheck className="mr-1.5 h-4 w-4" /> {copy.markAllRead}
          </Button>
        </header>

        <div className="mt-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="inline-flex w-full rounded-xl bg-muted/60 p-1 sm:w-auto">
            <ViewButton active={activeView === "inbox"} icon={Bell} label={copy.inbox} onClick={() => setActiveView("inbox")} testId="notifications-tab-inbox" />
            <ViewButton active={activeView === "prefs"} icon={Settings2} label={copy.preferences} onClick={() => setActiveView("prefs")} testId="notifications-tab-prefs" />
          </div>

          {activeView === "inbox" && (
            <div className="flex gap-2 overflow-x-auto pb-1 lg:pb-0">
              {filters.map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => setActiveFilter(filter.id)}
                  className={`inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full px-3 text-xs font-medium transition-colors ${
                    activeFilter === filter.id
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted/55 text-muted-foreground hover:bg-muted hover:text-foreground"
                  }`}
                >
                  <span>{filter.label}</span>
                  <span className={`text-[10px] ${activeFilter === filter.id ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{filterCounts[filter.id] || 0}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {activeView === "inbox" ? (
          <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
            <section className="space-y-5">
              {grouped.map(([group, items]) => (
                <div key={group}>
                  <div className="mb-2 flex items-center gap-2 px-1">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{group}</div>
                    <div className="h-px flex-1 bg-border/70" />
                  </div>
                  <div className="overflow-hidden rounded-2xl border bg-card">
                    {items.map((notification) => (
                      <NotificationRow
                        key={notification.id}
                        notification={notification}
                        isSelected={selected?.id === notification.id}
                        isUnread={unreadIds.has(notification.id)}
                        onAction={handleNotificationAction}
                        onSelect={() => {
                          markNotificationRead(notification.id);
                          setSelected(notification);
                        }}
                      />
                    ))}
                  </div>
                </div>
              ))}

              {!filteredNotifications.length && (
                <div className="rounded-2xl border bg-card px-5 py-14 text-center">
                  <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/[0.07] text-primary">
                    <Bell className="h-5 w-5" />
                  </div>
                  <div className="mt-3 text-sm font-semibold">{copy.noNotificationsFound}</div>
                  <p className="mt-1 text-xs text-muted-foreground">{copy.noNotificationsDescription}</p>
                </div>
              )}
            </section>

            <NotificationDetail selected={selected} detail={detail} loading={detailLoading} onAction={handleNotificationAction} />
          </div>
        ) : (
          <section className="mt-6 max-w-3xl">
            <div className="mb-3 flex items-center gap-2 px-1">
              <SlidersHorizontal className="h-4 w-4 text-primary" />
              <div className="text-sm font-semibold">{copy.deliveryPreferences}</div>
              <div className="h-px flex-1 bg-border/70" />
            </div>
            <div className="space-y-2">
              {localizedPrefs.map((preference) => (
                <PreferenceRow
                  key={preference.id}
                  preference={preference}
				  onChange={async (enabled) => {
					const nextPrefs = prefs.map((item) => item.id === preference.id ? { ...item, enabled } : item);
					setPrefs(nextPrefs);
					try {
					  const changed = nextPrefs.find((item) => item.id === preference.id);
					  await updateNotificationPreferences(session?.sessionId, { routes: changed ? [{ eventType: changed.id, enabled: changed.enabled, email: changed.email, telegram: changed.telegram }] : [] });
                      await reload();
                      toast.success(`${preference.label} ${enabled ? copy.enabled : copy.disabled}`);
                    } catch (error) {
                      toast.error(error?.message || copy.updatePreferencesFailed);
                      await reload();
                    }
                  }}
                />
              ))}
            </div>
            {isAccountOwner ? (
              <TeamReportDelivery
                busyRecipientId={busyRecipientId}
                copy={copy}
                onRetry={loadReportRecipients}
                onToggle={handleReportRecipientToggle}
                onUpgrade={() => navigate("/subscription")}
                recipients={reportRecipients.filter((recipient) => !recipient.isOwner)}
                state={reportRecipientsState}
              />
            ) : null}
          </section>
        )}
      </div>
    </PageContainer>
  );
}

function TeamReportDelivery({ busyRecipientId, copy, onRetry, onToggle, onUpgrade, recipients, state }) {
  return (
    <section className="mt-7 overflow-hidden rounded-2xl border bg-card shadow-[0_18px_50px_-42px_rgba(15,70,68,0.5)]" data-testid="team-report-delivery">
      <div className="flex items-start gap-3 border-b border-border/60 px-5 py-4">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/[0.08] text-primary"><Users className="h-[18px] w-[18px]" /></span>
        <div className="min-w-0">
          <h2 className="text-sm font-semibold">{copy.teamReportsTitle}</h2>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">{copy.teamReportsDescription}</p>
        </div>
      </div>

      {state === "loading" || state === "idle" ? (
        <div className="flex items-center justify-center gap-2 px-5 py-10 text-xs text-muted-foreground"><LoaderCircle className="h-4 w-4 animate-spin" /> {copy.teamReportsLoading}</div>
      ) : state === "locked" ? (
        <div className="flex flex-col gap-4 px-5 py-5 sm:flex-row sm:items-center">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-muted text-muted-foreground"><LockKeyhole className="h-4 w-4" /></span>
          <div className="min-w-0 flex-1"><div className="text-sm font-semibold">{copy.teamReportsLockedTitle}</div><p className="mt-1 text-xs leading-5 text-muted-foreground">{copy.teamReportsLockedDescription}</p></div>
          <Button variant="outline" size="sm" onClick={onUpgrade} className="shrink-0 rounded-xl">{copy.reviewPlans}</Button>
        </div>
      ) : state === "error" ? (
        <div className="flex flex-col items-center px-5 py-9 text-center"><AlertTriangle className="h-5 w-5 text-warning" /><div className="mt-3 text-sm font-semibold">{copy.teamReportsLoadFailed}</div><Button variant="outline" size="sm" onClick={onRetry} className="mt-4 rounded-xl"><RefreshCcw className="mr-1.5 h-3.5 w-3.5" />{copy.tryAgain}</Button></div>
      ) : recipients.length === 0 ? (
        <div className="px-5 py-9 text-center"><Users className="mx-auto h-6 w-6 text-muted-foreground/45" /><div className="mt-3 text-sm font-semibold">{copy.noTeamRecipients}</div><p className="mt-1 text-xs text-muted-foreground">{copy.noTeamRecipientsDescription}</p></div>
      ) : (
        <div>
          <div className="grid grid-cols-[minmax(0,1fr)_72px_72px_72px] items-center bg-muted/25 px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            <span>{copy.participant}</span><span className="text-center">{copy.daily}</span><span className="text-center">{copy.weekly}</span><span className="text-center">{copy.monthly}</span>
          </div>
          <div className="divide-y divide-border/60">
            {recipients.map((recipient) => {
              const busy = busyRecipientId === recipient.userId;
              const initials = recipient.name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "TM";
              return (
                <div key={recipient.userId} className="grid grid-cols-[minmax(0,1fr)_72px_72px_72px] items-center px-5 py-3.5" data-testid={`team-report-recipient-${recipient.userId}`}>
                  <div className="flex min-w-0 items-center gap-3 pr-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/[0.08] text-[11px] font-semibold text-primary">{initials}</span>
                    <div className="min-w-0"><div className="truncate text-sm font-medium">{recipient.name}</div><div className="mt-0.5 truncate text-[11px] text-muted-foreground">{recipient.role}{recipient.email ? ` · ${recipient.email}` : ""}</div></div>
                  </div>
                  {[["dailyEnabled", copy.daily], ["weeklyEnabled", copy.weekly], ["monthlyEnabled", copy.monthly]].map(([period, label]) => (
                    <div key={period} className="flex justify-center">
                      <Switch aria-label={`${recipient.name} ${label}`} checked={Boolean(recipient[period])} disabled={Boolean(busyRecipientId)} onCheckedChange={() => onToggle(recipient, period)} data-testid={`team-report-${recipient.userId}-${period}`} />
                    </div>
                  ))}
                  {busy ? <span className="sr-only" aria-live="polite">{copy.saving}</span> : null}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}

function ViewButton({ active, icon: Icon, label, onClick, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`inline-flex h-9 flex-1 items-center justify-center gap-2 rounded-lg px-4 text-xs font-semibold transition-colors sm:flex-none ${
        active ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
      }`}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}

function NotificationRow({ notification, isSelected, isUnread, onAction, onSelect }) {
  const { locale } = useLocalization();
  const Icon = ICON_BY_TYPE[notification.type] || Bell;
  return (
    <div
      className={`group relative overflow-hidden border-b last:border-b-0 transition-colors ${
        isSelected
          ? "bg-primary/[0.045]"
          : "bg-card hover:bg-muted/30"
      }`}
    >
      <button
        data-testid={`notification-${notification.id}`}
        onClick={onSelect}
        className="relative flex w-full items-start gap-3 px-4 py-3.5 text-left"
      >
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${TONE_MAP[notification.tone] || TONE_MAP.info}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                {isUnread ? <Circle className="h-2 w-2 shrink-0 fill-primary text-primary" /> : null}
                <div className="truncate text-sm font-semibold">{notification.title}</div>
              </div>
              <div className="mt-1 line-clamp-2 text-xs leading-5 text-muted-foreground">{notification.body}</div>
            </div>
			<span className="shrink-0 text-[11px] text-muted-foreground">{formatRelative(notificationTimestamp(notification), locale)}</span>
          </div>
        </div>
      </button>
      {notification.actionLabel ? (
        <div className="relative flex justify-end px-4 pb-2.5">
          <button
            onClick={() => onAction(notification)}
            className="inline-flex items-center gap-1.5 rounded-full bg-primary/[0.07] px-3 py-1.5 text-[11px] font-semibold text-primary transition-colors hover:bg-primary/12"
          >
            {notification.actionLabel}
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : null}
    </div>
  );
}

function NotificationDetail({ selected, detail, loading, onAction }) {
  const { language, locale, messages } = useLocalization();
  const copy = messages.notificationsPage;
  const detailCopy = parityCopy[language].detail;
  if (!selected) {
    return (
      <aside className="hidden h-fit rounded-2xl border bg-card p-8 text-center text-muted-foreground lg:block">
        <Bell className="mx-auto h-7 w-7 opacity-50" />
        <div className="mt-3 text-sm">{copy.selectNotification}</div>
      </aside>
    );
  }

  const notification = detail?.notification || selected;
  const report = detail?.report;
  const snapshot = report?.snapshot;
  const narrative = report?.narrative;
  const Icon = ICON_BY_TYPE[notification.type] || Bell;
  return (
    <aside className="relative h-fit overflow-hidden rounded-2xl border bg-card p-5 shadow-[0_10px_35px_-28px_rgba(15,70,68,0.45)] lg:sticky lg:top-24">
      <div className="flex items-start justify-between gap-4">
        <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${TONE_MAP[selected.tone] || TONE_MAP.info}`}>
          <Icon className="h-4 w-4" />
        </div>
		<span className="text-xs text-muted-foreground">{formatRelative(notificationTimestamp(notification), locale)}</span>
      </div>
      <h2 className="mt-4 text-base font-semibold tracking-tight">{notification.title}</h2>
      <p className="mt-2 text-sm leading-6 text-muted-foreground">{notification.body}</p>
      {loading ? <div className="mt-5 h-24 animate-pulse rounded-xl bg-muted/60" /> : null}
      {snapshot ? <ReportSummary snapshot={snapshot} narrative={narrative} report={report} locale={locale} copy={detailCopy} /> : <OperationalFacts data={notification.data} locale={locale} copy={detailCopy} />}
      <div className="mt-4 border-t border-border pt-3 text-[11px] text-muted-foreground">
		{formatDateTime(notificationTimestamp(notification), locale)}
      </div>
      {selected.actionLabel ? (
        <Button className="mt-4 h-10 w-full rounded-xl" data-testid="notification-action" onClick={() => onAction(selected)}>
          {selected.actionLabel}
          <ArrowRight className="ml-1.5 h-4 w-4" />
        </Button>
      ) : null}
    </aside>
  );
}

function ReportSummary({ snapshot, narrative, report, locale, copy }) {
  const currency = snapshot.currencyCode || "NGN";
  const money = (value) => new Intl.NumberFormat(locale, { style: "currency", currency, maximumFractionDigits: 0 }).format(Number(value) || 0);
  return (
    <div className="mt-5 space-y-4">
      <div className="grid grid-cols-2 gap-2">
        <Fact label={copy.sales} value={money(snapshot.totalSales)} />
        <Fact label={copy.orders} value={Number(snapshot.totalOrders || 0).toLocaleString(locale)} />
        <Fact label={copy.expenses} value={money(snapshot.totalExpenses)} />
        <Fact label={copy.operatingProfit} value={money(snapshot.operatingProfit ?? snapshot.netProfit)} />
      </div>
      {narrative?.executiveSummary ? <p className="rounded-xl bg-muted/45 p-3 text-xs leading-5 text-muted-foreground">{narrative.executiveSummary}</p> : null}
      {narrative?.recommendedAction ? <div><div className="text-[10px] font-semibold uppercase tracking-wider text-primary">{copy.nextMove}</div><p className="mt-1 text-xs leading-5">{narrative.recommendedAction}</p></div> : null}
      {report?.pdfUrl ? <a href={report.pdfUrl} target="_blank" rel="noreferrer" className="inline-flex text-xs font-semibold text-primary hover:underline">{copy.openPdf}</a> : null}
    </div>
  );
}

function OperationalFacts({ data = {}, locale, copy }) {
  const facts = [
    [copy.currentStock, data.currentStock], [copy.lowStock, data.lowStockAt],
    [copy.restock, data.minimumSuggestedQuantity], [copy.unitsSold, data.soldLastWindow],
    [copy.weeklyDemand, data.averageWeeklyDemand], [copy.confidence, data.confidencePercent == null ? null : `${Number(data.confidencePercent).toLocaleString(locale)}%`],
  ].filter(([, value]) => value !== null && value !== undefined && value !== "");
  const recommendation = data.recommendation || data.reason;
  if (!facts.length && !recommendation) return null;
  return <div className="mt-5"><div className="grid grid-cols-2 gap-2">{facts.map(([label, value]) => <Fact key={label} label={label} value={String(value)} />)}</div>{recommendation ? <p className="mt-3 rounded-xl bg-primary/[0.05] p-3 text-xs leading-5">{recommendation}</p> : null}</div>;
}

function Fact({ label, value }) { return <div className="rounded-xl border bg-background p-3"><div className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div><div className="mt-1.5 truncate text-sm font-semibold">{value}</div></div>; }

function PreferenceRow({ preference, onChange }) {
  const { messages } = useLocalization();
  const copy = messages.notificationsPage;
  return (
    <div className="relative flex items-center justify-between gap-4 overflow-hidden rounded-2xl border bg-card px-4 py-3.5">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <div className="text-sm font-semibold">{preference.label}</div>
          <span className={`rounded-sm px-1.5 py-0.5 text-[10px] font-semibold ${preference.enabled ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
            {preference.enabled ? copy.on : copy.off}
          </span>
        </div>
        <div className="mt-1 text-xs leading-5 text-muted-foreground">{preference.description}</div>
      </div>
      <Switch
        data-testid={`notification-pref-${preference.id}`}
        checked={preference.enabled}
        disabled={!preference.enabled && ((preference.email && !preference.pushAvailable) || (preference.telegram && !preference.telegramConnected))}
        onCheckedChange={onChange}
      />
    </div>
  );
}
